import React, { type JSX } from 'react';

import CustomIcon from '../../../../components/CustomIcon';
import { WidgetFooter, WidgetHeader } from 'pages/WidgetManagement/common';
import { useSortedData } from '../../../../hooks/useSortedData';
import { InfoListItem } from '@brightlayer-ui/react-components';
import { Avatar, Divider, Paper, Stack } from '@mui/material';
import { CustomTransComponent, GroupPath } from 'components';
import { Update } from '@mui/icons-material';
import { CustomEmptyState } from 'components/CustomEmptyState';
import { useTypedSelector } from '@fiji/common';
import { useNavigate, useParams } from 'react-router-dom';
import { selectCurrentPermission } from '@fiji/common/src/features/profile/profileSlice';
import { useRBAC } from 'hooks';
import { EventListingItem } from 'components/EventListingItem/EventListingItem';

type UpcomingItem = {
    id: string | number;
    within?: number | string;
    type?: string;
    icon?: { web?: { name?: string; family?: string; style?: { color?: string; backgroundColor?: string } } };
    status?: {
        icon?: { web?: { name?: string; family?: string; style?: { color?: string; backgroundColor?: string } } };
    };
    deviceId?: string;
    deviceName?: string;
    groupPath?: any[];
    indicator?: string;
};

type UpcomingWidgetData = {
    maxCount?: number;
    secondary?: UpcomingItem[];
    viewTitleBar?: boolean;
    viewAllButton?: boolean;
    total?: number | string;
};

type UpcomingWidgetProps = {
    widgetData?: UpcomingWidgetData;
    mode?: string;
};

type UpcomingRowProps = {
    upcoming: UpcomingItem;
    mode?: string;
    currentRealmName: string;
    deviceId?: string;
    navigate: ReturnType<typeof useNavigate>;
    canViewPrediction: boolean;
};

const getWithinDays = (value: number | string | undefined): number => {
    const parsedWithin = Number(value);
    return Number.isFinite(parsedWithin) ? parsedWithin : 0;
};

const getWidgetCount = (
    items: UpcomingItem[],
    maxCount: number | undefined,
    total: number | string | undefined
): string => {
    const maxVisibleItems = maxCount ?? items.length;
    const totalVisibleCount = Math.min(items.length, maxVisibleItems);
    const totalFromApi = Number(total);
    const totalCount = Number.isFinite(totalFromApi) ? totalFromApi : items.length;
    return `${totalVisibleCount}/${totalCount}`;
};

const getWebIcon = (
    upcoming: UpcomingItem
): { name?: string; family?: string; style?: { color?: string; backgroundColor?: string } } | undefined => {
    if (upcoming.icon && upcoming.icon.web) {
        return upcoming.icon.web;
    }

    if (upcoming.status && upcoming.status.icon && upcoming.status.icon.web) {
        return upcoming.status.icon.web;
    }

    return undefined;
};

const getRowIcon = (upcoming: UpcomingItem): JSX.Element | undefined => {
    const webIcon = getWebIcon(upcoming);
    if (!webIcon) {
        return undefined;
    }

    const iconStyle = webIcon.style ?? {};
    const iconColor = iconStyle.color ?? '#727E84';
    const iconBackground = iconStyle.backgroundColor ?? '#FFFFFF';
    const iconName = webIcon.name ?? '';
    const iconFamily = webIcon.family ?? 'material-ui';

    return (
        <Avatar sx={{ bgcolor: iconBackground }}>
            <CustomIcon
                iconName={iconName}
                family={iconFamily}
                iconProps={{
                    sx: {
                        color: iconColor,
                    },
                }}
            />
        </Avatar>
    );
};

const getUpcomingItems = (sortedData: any, secondary: UpcomingItem[]): UpcomingItem[] => {
    if (Array.isArray(sortedData?.secondary)) {
        return sortedData.secondary;
    }

    return secondary;
};

const getListHeight = (mode: string | undefined, viewAllButton: boolean | undefined): string => {
    if (mode === 'edit' || viewAllButton) {
        return '97px';
    }

    return '49px';
};

const UpcomingRow = ({
    upcoming,
    mode,
    currentRealmName,
    deviceId,
    navigate,
    canViewPrediction,
}: UpcomingRowProps): JSX.Element => {
    const withinDays = getWithinDays(upcoming.within);
    const isNow = withinDays <= 0;

    const handleClick = (): void => {
        if (mode === 'view' && canViewPrediction) {
            navigate(`/${currentRealmName}/upcomingEventsDetails/${upcoming.deviceId ?? deviceId}/${upcoming.id}`);
        }
    };

    return (
        <InfoListItem
            key={upcoming.id}
            data-testid={`upcoming-widget-row-${upcoming.id}`}
            title={<EventListingItem column="type" within={withinDays} type={upcoming.type} isNow={isNow} />}
            onClick={handleClick}
            subtitle={[
                <GroupPath
                    key={upcoming.id}
                    deviceName={upcoming.deviceName}
                    pathName={upcoming.groupPath ?? []}
                    width={'220px'}
                />,
            ]}
            divider={'full'}
            iconAlign="center"
            statusColor={upcoming.indicator}
            leftComponent={<EventListingItem column="within" within={withinDays} isNow={isNow} />}
            icon={getRowIcon(upcoming)}
            chevron={canViewPrediction}
            ripple={canViewPrediction}
        />
    );
};

const Component = ({ widgetData = {}, mode }: UpcomingWidgetProps): JSX.Element => {
    const { maxCount, secondary = [], viewTitleBar, viewAllButton, total } = widgetData;
    const { deviceId } = useParams();
    const { sortedData } = useSortedData(widgetData, ['secondary'], 'ASC', 'upcoming');
    const currentRealmName = useTypedSelector((state: any) => state.common.selectedRealm);
    const permissions = useTypedSelector(selectCurrentPermission);
    const { hasPermission } = useRBAC(permissions);
    const canViewPrediction = hasPermission('prediction-notifications_view-prediction');
    const navigate = useNavigate();
    const upcomingItems = getUpcomingItems(sortedData, secondary);
    const hasItems = upcomingItems.length > 0;
    const headerCount = getWidgetCount(upcomingItems, maxCount, total);
    const listHeight = getListHeight(mode, viewAllButton);
    const visibleCount = maxCount ?? upcomingItems.length;
    const visibleItems = upcomingItems.slice(0, visibleCount);

    return (
        <Paper sx={{ height: '100%' }} data-testid="upcoming-widget-root">
            {viewTitleBar && (
                <WidgetHeader mode={mode} sortedData={sortedData} widgetData={widgetData} count={headerCount} />
            )}
            {mode !== 'edit' && viewTitleBar && <Divider />}
            <Stack
                data-testid="upcoming-widget-list-stack"
                sx={{
                    height: `calc(100% - ${listHeight})`,
                    overflow: 'auto',
                }}
            >
                {hasItems ? (
                    visibleItems.map(
                        (upcoming: UpcomingItem): JSX.Element => (
                            <UpcomingRow
                                key={upcoming.id}
                                upcoming={upcoming}
                                mode={mode}
                                currentRealmName={currentRealmName}
                                deviceId={deviceId}
                                navigate={navigate}
                                canViewPrediction={canViewPrediction}
                            />
                        )
                    )
                ) : (
                    <CustomEmptyState
                        data-testid="upcoming-widget-empty-state"
                        icon={<Update sx={{ fontSize: '100px', color: '#727E84' }} />}
                        title={<CustomTransComponent translationKey={'UPCOMING:NO_UPCOMING_EVENTS'} />}
                        description={
                            <CustomTransComponent translationKey={'UPCOMING:NO_UPCOMING_EVENTS_DESCRIPTION'} />
                        }
                    />
                )}
            </Stack>
            <WidgetFooter widgetData={widgetData} ids={[]} mode={mode} />
        </Paper>
    );
};

export default Component;
